/**
 *
 *  CalendarMonth
 *  TruBe Date Time Calendar Month Component
 *
 *  Created by Vladimir Shishlyannikov on 01/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useEvent } from "../../../hooks/useEvent/useEvent";
import useForwardRef from "../../../hooks/useForwardRef/useForwardRef";
import useDeviceCapabilities from "../../../hooks/useDeviceCapabilities/useDeviceCapabilities";
import moment from "moment";

import ContentScroller from "../../ContentScroller/ContentScroller";
import PrevButton from "../PrevButton/PrevButton";
import NextButton from "../NextButton/NextButton";

import useContentScrollerControls from "../../../hooks/useContentScrollerControls/useContentScrollerControls";
import useButtonsHidden from "../useButtonsHidden/useButtonsHidden";
import { getScrollTarget } from "../../../utils/getScrollTarget";

import { getMomentWithTz } from "../../../utils/date";
import isSameDay from "../helpers/isSameDay";
import getModifierKey from "../helpers/getModifierKey";
import getModifierKeyFull from "../helpers/getModifierKeyFull";
import { availabilityCalendar as calendarConstants } from "../../../constants";

const AUTOSCROLL_TIMEOUT = 1000;

function getMonthChunks(months, min, region) {
  const dates = [];

  months.forEach((date, index) => {
    dates.push([]);

    const startDate = (
        index === 0
          ? (min ? min : getMomentWithTz(new Date(), region))
              .clone()
              .startOf("day")
          : date.clone().startOf("month")
      ).minutes(330),
      endDate = startDate.clone().endOf("month").minutes(0).hour(23),
      daysCount = endDate.diff(startDate, "days"),
      start = startDate.clone();

    for (let day = 0; day <= daysCount; day += 1) {
      dates[dates.length - 1].push(start.clone());

      start.add(1, "day");
    }
  });

  return dates;
}

function getMonthOffset(monthIndex, monthChunks) {
  return monthIndex === 0
    ? 0
    : monthIndex === 1
    ? monthChunks[0].length
    : monthChunks[0].length + monthChunks[1].length;
}

const defaultProps = {
  horizontalMonthPadding: 13,
  isVisible: true,
  enableOutsideDays: false,
  modifiers: {},
  orientation: calendarConstants.CalendarOrientation.HORIZONTAL,
  daySize: calendarConstants.DEFAULT_CALENDAR_DAY_SIZE,
  onDayClick() {},
  onDayMouseEnter() {},
  onDayMouseLeave() {},
  onMonthSelect() {},
  onYearSelect() {},
  firstDayOfWeek: undefined,
  setMonthTitleHeight: undefined,
  nextFocusedDate: undefined,
  focusedDate: undefined,
  isFocused: false,
  dayAriaLabelFormat: undefined,
  verticalBorderSpacing: undefined,
};

const CalendarMonthScroller = ({
  isLoading,
  dayAriaLabelFormat,
  region,
  daySize,
  focusedDate,
  // enableOutsideDays,
  modifiers,
  month,
  months,
  onDayClick,
  onDayMouseEnter,
  onDayMouseLeave,
  onScrollerMounted,
  onDayVisibleChange,
  onMonthVisibleChange,
  renderCalendarDay,
  // page,
}) => {
  // eslint-disable-next-line
  const [monthChunks, setMonthChunks] = useState(
    getMonthChunks(months, null /*, enableOutsideDays*/, region)
  );
  const [scrollerMounted, setScrollerMounted] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const modifiersRef = useRef();
  const scrolledRef = useRef(false);
  const lastVisibleItemIndexesRef = useRef([]);

  useEffect(() => {
    modifiersRef.current = modifiers;
  }, [modifiers]);

  const { supportsTouchCapableExperience } = useDeviceCapabilities(),
    slotsCount = monthChunks.reduce((acc, item) => {
      acc += item.length;

      return acc;
    }, 0),
    [_supportsTouchCapableExperience, setSupportsTouchCapableExperience] =
      useState(supportsTouchCapableExperience ? 1 : 0);

  useEffect(() => {
    setSupportsTouchCapableExperience(supportsTouchCapableExperience ? 1 : 0);
  }, [supportsTouchCapableExperience]);

  const contentScrollerRef = useRef(null),
    [_contentScrollerRef, setContentScrollerRef] =
      useForwardRef(contentScrollerRef);

  const [hiddenPrev, hiddenNext] = useButtonsHidden({
      contentScrollerApi: _contentScrollerRef,
      itemCount: slotsCount,
      isLoading: isLoading,
    }),
    { onPressForward, onPressReverse, scrollTo } = useContentScrollerControls({
      contentScrollerApi: _contentScrollerRef,
      loop: false,
      unit: "slide",
      getScrollTarget,
    });

  useEffect(() => {
    if (_contentScrollerRef) {
      setScrollerMounted(true);
      onScrollerMounted == null || onScrollerMounted(_contentScrollerRef);
    }
  }, [onScrollerMounted, _contentScrollerRef]);

  const visibleItemsCount =
    _contentScrollerRef && _contentScrollerRef.getVisibleCount();

  useEffect(() => {
    if (scrolledRef.current) {
      return;
    }

    setTimeout(() => {
      const now = getMomentWithTz(new Date(), region);

      if (scrolledRef.current) {
        return;
      }

      var index = 0;
      var scrollToElement = 0;

      for (const prop in modifiersRef.current) {
        for (const monthVal in modifiersRef.current[prop]) {
          let element = modifiersRef.current[prop][monthVal];
          if (
            element.has("selected") ||
            element.has("selected-start") ||
            element.has("selected-end")
          ) {
            console.log(element);

            scrollToElement = index + 1;
          }

          if (
            getMomentWithTz(monthVal, "YYYY-MM-DD", region).isSameOrAfter(now)
          ) {
            index++;
          }
        }
      }

      if (!_contentScrollerRef) {
        return;
      }

      var nextElementKey =
        scrollToElement - Math.floor(_contentScrollerRef.getVisibleCount() / 2);

      if (nextElementKey < 0) {
        nextElementKey = 0;
      }

      scrollTo({
        nextElementKey: nextElementKey,
        spaceBetweenItems: 0,
        shouldUseScrollIntoView: false,
      });

      setScrolled(true);
      scrolledRef.current = true;
    }, AUTOSCROLL_TIMEOUT);
  }, [scrollerMounted, visibleItemsCount, scrollTo]);

  const recalcReset = useEvent(() => {
    _contentScrollerRef &&
      _contentScrollerRef.recalc({
        reason: "reset",
      });
  });

  useEffect(() => {
    _contentScrollerRef && recalcReset();
  }, [recalcReset, _contentScrollerRef]);

  const recalcChildrenChangedEvent = useEvent(() => {
    _contentScrollerRef &&
      setTimeout(() =>
        _contentScrollerRef.recalc({
          reason: "children_changed",
        })
      );
  });

  useEffect(() => {
    _contentScrollerRef &&
      _supportsTouchCapableExperience > 0 &&
      recalcChildrenChangedEvent();
  }, [
    recalcChildrenChangedEvent,
    _contentScrollerRef,
    _supportsTouchCapableExperience,
  ]);

  const handleDayVisibleChange = (key, inView, element) => {
    const visibleItemIndexes = _contentScrollerRef.getVisibleItemIndexes();

    const beforeLastMonthEnd =
      monthChunks[0].length - 1 + monthChunks[1].length;
    const firstMonthEnd = monthChunks[0].length;

    const firstMonthEndIndex =
      firstMonthEnd + Math.floor(visibleItemIndexes.length / 2); // Adjust so that new month only starts when it occupies 5 or more slots
    const beforeLastMonthIndex =
      beforeLastMonthEnd + Math.floor(visibleItemIndexes.length / 2);

    if (inView && key >= beforeLastMonthIndex) {
      onMonthVisibleChange(2);
    } else if (
      inView &&
      key >= firstMonthEndIndex &&
      key <= beforeLastMonthEnd - 1
    ) {
      onMonthVisibleChange(1);
    } else if (
      inView &&
      key >= 0 &&
      key <= monthChunks[0].length - 1 &&
      !visibleItemIndexes.includes(firstMonthEndIndex) &&
      !visibleItemIndexes.includes(beforeLastMonthEnd)
    ) {
      onMonthVisibleChange(0);
    }

    if (key === 0 && !inView) {
      scrolledRef.current = true;
    }

    lastVisibleItemIndexesRef.current = visibleItemIndexes;
  };

  return (
    <div className="calendar-month">
      <ContentScroller
        autoRecalcOnMount={false}
        controls={
          !isLoading && (
            <PrevButton
              onPress={(...args) => {
                scrolledRef.current = true;
                onPressReverse(...args);
              }}
              className="md:tbk-bottom-2 md:tbk-left-[-48px]"
              buttonClassName="tbk-mb-1.5"
              visible={!hiddenPrev}
            />
          )
        }
        trailingControls={
          !isLoading && (
            <NextButton
              onPress={(...args) => {
                scrolledRef.current = true;
                onPressForward(...args);
              }}
              className="md:tbk-bottom-2 md:tbk-right-[-48px]"
              buttonClassName="tbk-mb-1.5"
              visible={!hiddenNext}
            />
          )
        }
        description=""
        ref={setContentScrollerRef}
        ariaScrollerRole="radiogroup"
        style={{
          position: "relative",
        }}
      >
        {monthChunks.map((monthVal, monthIndex) => {
          const _modifiers = modifiers[getModifierKey(monthVal[0])];
          const monthOffset = getMonthOffset(monthIndex, monthChunks);

          return monthVal.map((day, dayIndex) =>
            day ? (
              renderCalendarDay({
                key: `${monthIndex}-${dayIndex}`,
                day: day,
                daySize: daySize,
                contentScrollerApi: _contentScrollerRef,
                onDayVisibleChange: handleDayVisibleChange,
                itemKey: monthOffset + dayIndex,
                isOutsideDay: !day || day.month() !== month.month(),
                isFocused: isSameDay(day, focusedDate || null),
                onDayMouseEnter: onDayMouseEnter,
                onDayMouseLeave: onDayMouseLeave,
                onDayClick: onDayClick,
                modifiers: _modifiers[getModifierKeyFull(day)],
                ariaLabelFormat: dayAriaLabelFormat,
              })
            ) : (
              <div key={`${monthIndex}-${dayIndex}`} />
            )
          );
        })}
      </ContentScroller>
    </div>
  );
};

CalendarMonthScroller.defaultProps = defaultProps;

CalendarMonthScroller.propTypes = {
  month: PropTypes.instanceOf(moment),
  isVisible: PropTypes.bool,
  enableOutsideDays: PropTypes.bool,
  modifiers: PropTypes.object,
  onDayMouseEnter: PropTypes.func,
  onDayMouseLeave: PropTypes.func,
  onDayClick: PropTypes.func,
  renderCalendarDay: PropTypes.func,
  daySize: PropTypes.number,
  focusedDate: PropTypes.instanceOf(moment),
  dayAriaLabelFormat: PropTypes.string,
};

export default CalendarMonthScroller;
